/**
 * The Hub link map (D-089, D-511, D-530): ONE parser and builder for every link that leaves the
 * web app — push payload `url`s, email permalinks, the tasks DTO permalink and `taskDeepLink`, the
 * mobile app (`apps/mobile/src/links/hubLinks.ts` is a thin wrapper) and the desktop bridge
 * (`apps/desktop/bridge`, whose test holds the desktop's fixture to be a subset of this table).
 * Its table is `links.fixtures.json`, read by `links.test.ts`; the desktop Rust parser keeps its own
 * fixture as the subset it accepts and is not changed in Phase 3 (D-511).
 *
 * Routes (paths are the same on `comskyorbit://`, `https://<hub web origin>` and in-app):
 *
 *   /c/{cid}                             conversation
 *   /c/{cid}/m/{mid}[?thread={rootId}]   a message (a thread reply when `thread` is set)
 *   /t/{T-1042 | 1042}                   a task (by number)
 *   /tasks?task=T-1042                   the alias Phase 4 emits (`taskDeepLink`); parsed, never built
 *                                        by `hubPath` (which writes `/t/T-1042`)
 *   /daily/agenda/{YYYY-MM-DD}           the agenda form of that day
 *   /daily/report/{YYYY-MM-DD}           the report form of that day
 *   /call/{id}                           a call: the phone opens its call screen (wave 4B, D-952);
 *                                        the web opens the call's message in its conversation
 *   /performance/{page}[/{id}]           wave 4 (D-801): scorecard[/{userId}], weekly[/{reviewId}],
 *                                        meetings[/{occurrenceId}], training[/{assignmentId}],
 *                                        appraisals[/{appraisalId}], drafts (the AI review queue)
 *   /calendar                            wave 5 (D-1041, D-1044): the calendar page
 *
 * Public pages (wave 5, D-1013) are not app routes and never parse as one: the personal guest
 * page `/m#t=<token>` (the token stays in the fragment, which no server sees), a webinar's
 * registration page `/w/<slug>` and a booking page `/b/<slug>`. Their builders are
 * {@link publicMeetingInviteUrl}, {@link publicWebinarUrl} and {@link publicBookingUrl}.
 *
 * Parsing rules (D-530):
 * - Ids are UUIDs of any version, lowercased on the way out; a task is a positive number of at
 *   most nine digits (`T-`/`t-` optional); a date is a real calendar day. Anything else, and every
 *   unknown path, is not a route. Route words (`c`, `m`, `t`, `tasks`, `daily`, `agenda`, `report`,
 *   `call`) are lower case; the scheme and the host are case-insensitive.
 * - Segments are percent-decoded before they are checked, so `%2e%2e` or `%2F` never reach a route.
 *   Empty segments are skipped (`///c/…`, a trailing `/`); the fragment is dropped.
 * - Query parameters other than `thread` (and `task` on `/tasks`) are ignored; the first
 *   occurrence of a parameter wins. A malformed or empty `thread` is dropped, so the message itself
 *   still opens; `thread` on any other route is ignored. (The desktop shell rejects all of these;
 *   it is the stricter subset.)
 * - An `https://` or `http://` link is a Hub link only on one of `options.webOrigins` (scheme, host
 *   and port compared; default ports and letter case ignored; no userinfo).
 * - Nothing here uses `URL` or the clock, so it behaves the same in React Native and in Node.
 */

export const HUB_LINK_SCHEME = 'comskyorbit';

/**
 * Where a performance link opens (wave 4, D-801): the same path segments as the web routes under
 * `/performance/`. The id is a person (`scorecard`, `skills`), a weekly review, a meeting
 * occurrence, a training assignment or an appraisal; `drafts` has none. Pay never has a link: it
 * carries no deep link in a push or an email (D-081). `skills` with a person opens that person's
 * skill profile (the web app redirects `/performance/skills/{userId}` to `…/people/{userId}`).
 */
export const PERFORMANCE_LINK_PAGES = [
  'scorecard',
  'weekly',
  'meetings',
  'training',
  'appraisals',
  'drafts',
  'skills',
] as const;
export type PerformanceLinkPage = (typeof PERFORMANCE_LINK_PAGES)[number];

export type HubLinkRoute =
  | { kind: 'conversation'; conversationId: string }
  | {
      kind: 'message';
      conversationId: string;
      messageId: string;
      /** The thread root for a thread reply, else null. */
      threadRootId: string | null;
    }
  | { kind: 'task'; taskNumber: number }
  | { kind: 'daily'; form: 'agenda' | 'report'; date: string }
  | { kind: 'call'; callId: string }
  | { kind: 'performance'; page: PerformanceLinkPage; id: string | null }
  | { kind: 'calendar' };

export type HubLinkKind = HubLinkRoute['kind'];

export interface HubLinkOptions {
  /** Web origins whose `https://` links are Hub links (normalised, no trailing slash). */
  webOrigins: readonly string[];
}

/** What a URL (or in-app path) handed to an app is, for deciding what to do with it. */
export type HubLinkClassification =
  /** A Hub link that names a route. */
  | { kind: 'route'; route: HubLinkRoute }
  /** Ours (the scheme, a web origin or an in-app path), but no route: an unknown or malformed page. */
  | { kind: 'unsupported' }
  /** Not a Hub link at all (another scheme or host, a dev client's own URLs, plain text, …). */
  | { kind: 'foreign' };

/** Longer input is never a Hub route (the longest real link is about 200 characters). */
export const HUB_LINK_MAX_LENGTH = 2048;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** `T-1042`, `t-1042`, `1042` (the same grammar as `parseTaskNumber` in ./tasks.ts). */
const TASK_REF_RE = /^(?:[Tt]-)?(\d{1,9})$/;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const SCHEME_RE = /^([a-z][a-z0-9+.-]*):([\s\S]*)$/i;
const AUTHORITY_RE = /^\/\/([^/?#]*)([\s\S]*)$/;
const ORIGIN_RE = /^(https?):\/\/([^/?#@\s]+)\/*$/i;

function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/** A decoded segment or parameter as a lower-case UUID, else null (never decodes a second time). */
function uuidOrNull(decoded: string | null | undefined): string | null {
  return decoded !== null && decoded !== undefined && UUID_RE.test(decoded)
    ? decoded.toLowerCase()
    : null;
}

function taskNumberOrNull(value: string | undefined): number | null {
  if (value === undefined) return null;
  const match = TASK_REF_RE.exec(value);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function isCalendarDate(value: string | undefined): value is string {
  if (value === undefined) return false;
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const daysInMonth = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!;
  return day <= daysInMonth;
}

/** The raw (still encoded) value of the first `name` parameter; `''` for `name` or `name=`. */
function queryParam(query: string, name: string): string | undefined {
  for (const part of query.split('&')) {
    const eq = part.indexOf('=');
    const key = safeDecode((eq === -1 ? part : part.slice(0, eq)).replace(/\+/g, ' '));
    if (key === name) return eq === -1 ? '' : part.slice(eq + 1);
  }
  return undefined;
}

/**
 * Parses an in-app path with an optional query (`/c/{cid}/m/{mid}?thread=…`; the leading slash is
 * optional). Unknown paths and malformed ids give null.
 */
export function parseHubPath(pathWithQuery: string): HubLinkRoute | null {
  if (pathWithQuery.length > HUB_LINK_MAX_LENGTH) return null;
  const hash = pathWithQuery.indexOf('#');
  const withoutHash = hash === -1 ? pathWithQuery : pathWithQuery.slice(0, hash);
  const q = withoutHash.indexOf('?');
  const rawPath = q === -1 ? withoutHash : withoutHash.slice(0, q);
  const query = q === -1 ? '' : withoutHash.slice(q + 1);

  const segments: string[] = [];
  for (const raw of rawPath.split('/')) {
    if (raw === '') continue;
    const decoded = safeDecode(raw);
    if (decoded === null) return null;
    segments.push(decoded);
  }

  switch (segments[0]) {
    case 'c': {
      const conversationId = uuidOrNull(segments[1]);
      if (!conversationId) return null;
      if (segments.length === 2) return { kind: 'conversation', conversationId };
      if (segments.length !== 4 || segments[2] !== 'm') return null;
      const messageId = uuidOrNull(segments[3]);
      if (!messageId) return null;
      const thread = queryParam(query, 'thread');
      // A malformed thread id is dropped: the message itself still opens.
      const threadRootId =
        thread === undefined || thread === '' ? null : uuidOrNull(safeDecode(thread));
      return { kind: 'message', conversationId, messageId, threadRootId };
    }
    case 't': {
      const taskNumber = taskNumberOrNull(segments[1]);
      return segments.length === 2 && taskNumber !== null ? { kind: 'task', taskNumber } : null;
    }
    case 'tasks': {
      // `/tasks?task=T-1042`, the alias Phase 4 emits; bare `/tasks` is a page, not a route.
      const ref = queryParam(query, 'task');
      const taskNumber = ref === undefined ? null : taskNumberOrNull(safeDecode(ref) ?? undefined);
      return segments.length === 1 && taskNumber !== null ? { kind: 'task', taskNumber } : null;
    }
    case 'daily': {
      const form = segments[1];
      const date = segments[2];
      if (segments.length !== 3 || (form !== 'agenda' && form !== 'report')) return null;
      return isCalendarDate(date) ? { kind: 'daily', form, date } : null;
    }
    case 'call': {
      const callId = uuidOrNull(segments[1]);
      return segments.length === 2 && callId ? { kind: 'call', callId } : null;
    }
    case 'calendar':
      return segments.length === 1 ? { kind: 'calendar' } : null;
    case 'performance': {
      const page = PERFORMANCE_LINK_PAGES.find((candidate) => candidate === segments[1]);
      if (!page) return null;
      if (segments.length === 2) return { kind: 'performance', page, id: null };
      const id = uuidOrNull(segments[2]);
      return segments.length === 3 && id && page !== 'drafts'
        ? { kind: 'performance', page, id }
        : null;
    }
    default:
      return null;
  }
}

/** `scheme://host[:port]` in lower case, without the scheme's default port. */
function normaliseOrigin(scheme: string, authority: string): string {
  const host = authority.toLowerCase();
  const defaultPort = scheme.toLowerCase() === 'https' ? ':443' : ':80';
  return `${scheme.toLowerCase()}://${host.endsWith(defaultPort) ? host.slice(0, -defaultPort.length) : host}`;
}

function normalisedOrigins(origins: readonly string[]): Set<string> {
  const result = new Set<string>();
  for (const origin of origins) {
    const match = ORIGIN_RE.exec(origin.trim());
    if (match) result.add(normaliseOrigin(match[1]!, match[2]!));
  }
  return result;
}

/**
 * Classifies a URL (or in-app path) that the OS, a push payload or an email handed to an app:
 * a route, a Hub link with no route, or not a Hub link. Only the scheme `comskyorbit`, the
 * `options.webOrigins` and paths that start with one `/` are ours.
 */
export function classifyHubLink(link: string, options: HubLinkOptions): HubLinkClassification {
  const trimmed = link.trim();
  const unsupportedOr = (route: HubLinkRoute | null): HubLinkClassification =>
    route ? { kind: 'route', route } : { kind: 'unsupported' };

  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    return unsupportedOr(parseHubPath(trimmed));
  }
  const match = SCHEME_RE.exec(trimmed);
  if (!match) return { kind: 'foreign' };
  const scheme = match[1]!.toLowerCase();
  const rest = match[2]!;

  if (scheme === HUB_LINK_SCHEME) {
    // comskyorbit://c/… (the first segment looks like a "host"), comskyorbit:///c/… or comskyorbit:c/…
    return unsupportedOr(parseHubPath(rest.replace(/^\/+/, '/')));
  }
  if (scheme !== 'https' && scheme !== 'http') return { kind: 'foreign' };
  const authority = AUTHORITY_RE.exec(rest);
  if (!authority || authority[1]!.includes('@')) return { kind: 'foreign' };
  if (!normalisedOrigins(options.webOrigins).has(normaliseOrigin(scheme, authority[1]!))) {
    return { kind: 'foreign' };
  }
  return unsupportedOr(parseHubPath(authority[2]!));
}

/**
 * Parses a `comskyorbit://…` URL, an `https://` URL on one of `options.webOrigins`, or an in-app
 * path (starting with `/`). Other URLs, and Hub links that name no route, give null.
 */
export function parseHubLink(link: string, options: HubLinkOptions): HubLinkRoute | null {
  const classified = classifyHubLink(link, options);
  return classified.kind === 'route' ? classified.route : null;
}

/** The canonical in-app path of a route (`/c/{cid}/m/{mid}?thread={rootId}`, `/t/T-1042`). */
export function hubPath(route: HubLinkRoute): string {
  switch (route.kind) {
    case 'conversation':
      return `/c/${route.conversationId.toLowerCase()}`;
    case 'message': {
      const path = `/c/${route.conversationId.toLowerCase()}/m/${route.messageId.toLowerCase()}`;
      return route.threadRootId ? `${path}?thread=${route.threadRootId.toLowerCase()}` : path;
    }
    case 'task':
      // `T-1042` like formatTaskNumber (not imported, so ./tasks.ts may build its links from here).
      return `/t/T-${route.taskNumber}`;
    case 'daily':
      return `/daily/${route.form}/${route.date}`;
    case 'call':
      return `/call/${route.callId.toLowerCase()}`;
    case 'performance':
      return route.id
        ? `/performance/${route.page}/${route.id.toLowerCase()}`
        : `/performance/${route.page}`;
    case 'calendar':
      return '/calendar';
  }
}

/**
 * The alias path of a task, `/tasks?task=T-1042`: what the web app's Tasks page opens the detail
 * panel from, and what Phase 4 notifications carry (`taskDeepLink` in ./tasks.ts). It parses to the
 * same `task` route as `/t/T-1042`.
 */
export function hubTaskAliasPath(taskNumber: number): string {
  return `/tasks?task=T-${taskNumber}`;
}

/** `comskyorbit://` + the canonical path (push payloads, the desktop): `comskyorbit://c/{cid}`. */
export function hubAppUrl(route: HubLinkRoute): string {
  return `${HUB_LINK_SCHEME}:/${hubPath(route)}`;
}

/** `{webOrigin}` + the canonical path (email permalinks, universal links). */
export function hubWebUrl(route: HubLinkRoute, webOrigin: string): string {
  return `${webOrigin.replace(/\/+$/, '')}${hubPath(route)}`;
}

// ---------------------------------------------------------------- public pages (wave 5, D-1013)

/** The answer an emailed Yes / Maybe / No button preselects (the page still asks for a click). */
export type PublicRsvpChoice = 'accepted' | 'tentative' | 'declined';

const trimOrigin = (webOrigin: string): string => webOrigin.replace(/\/+$/, '');

/**
 * A guest's personal page: `{origin}/m#t=<token>`, with `&r=<answer>` to preselect an answer
 * (§7.7) or `&c=1` for a registration's Confirm button (D-1032). The token rides in the fragment,
 * so it is never sent to a server or written to a log.
 */
export function publicMeetingInviteUrl(
  webOrigin: string,
  token: string,
  options: { response?: PublicRsvpChoice; confirm?: boolean } = {},
): string {
  const parts = [`t=${encodeURIComponent(token)}`];
  if (options.response) parts.push(`r=${options.response}`);
  if (options.confirm) parts.push('c=1');
  return `${trimOrigin(webOrigin)}/m#${parts.join('&')}`;
}

/** The guest join page (device check and lobby): `{origin}/m/join#t=<token>`. */
export function publicMeetingJoinUrl(webOrigin: string, token: string): string {
  return `${trimOrigin(webOrigin)}/m/join#t=${encodeURIComponent(token)}`;
}

/** A webinar's public registration page: `{origin}/w/<slug>`. Slugs are not secrets. */
export function publicWebinarUrl(webOrigin: string, slug: string): string {
  return `${trimOrigin(webOrigin)}/w/${encodeURIComponent(slug)}`;
}

/**
 * A booking page: `{origin}/b/<slug>`, or with `#confirm=<token>` for the emailed confirmation of
 * a held booking (D-1035).
 */
export function publicBookingUrl(webOrigin: string, slug: string, confirmToken?: string): string {
  const base = `${trimOrigin(webOrigin)}/b/${encodeURIComponent(slug)}`;
  return confirmToken ? `${base}#confirm=${encodeURIComponent(confirmToken)}` : base;
}

/** The private feed URL (the only path token, redacted in every log; https only, D-1023). */
export function calendarFeedUrl(apiOrigin: string, token: string): string {
  return `${trimOrigin(apiOrigin)}/api/v1/public/calendar-feeds/${encodeURIComponent(token)}/calendar.ics`;
}
